import { useEffect } from 'react'
import { isAxiosError, isPortalApiError } from '@afterpaytouch/portal-api/utils'
import { USER_JOURNEY_NAMES } from './constants'
import { useUserJourneyContext } from './UserJourneyContext'

/**
 * HTTP/network error → journey-signal classification (CASHDAP-3406).
 *
 * Only requests on the checkout critical path get tagged — tagging background calls was
 * Cash's biggest source of unknown-outcome noise. Friction never counts toward BAD in
 * the journey SLI (only frustrations and FAILED outcomes do), so misclassifying an
 * expected error as frustration here would dip the SLI without an outage — the #1
 * instrumentation error the UJ validation guide calls out.
 */

/** Canonical signal names from the Cash UJ instrumentation guide */
export const NETWORK_SIGNALS = {
  CLIENT: 'client-network-error',
  SERVER: 'server-network-error',
  UNKNOWN: 'unknown-network-error',
} as const

export type NetworkSignal = {
  signalType: 'friction' | 'frustration'
  signal: string
}

/**
 * The checkout critical path: portal-api requests the customer is blocked on between
 * opening checkout and confirming payment. Deliberately small — expand after week-6
 * tuning. Everything else (persistent-login resume/revoke, notification, order-status
 * personalization, topaz/rules config, analytics) stays untagged.
 */
const JOURNEY_RELEVANT_PATHS = [
  '/portal/consumers/checkout', // get/start/confirm/continue/update/payment-options, incl. /v2 variants
  '/portal/consumers/auth', // login + cash-auth ceremony — blocker steps
  '/portal/consumers/account', // account/identity lookup — gates cohort and payment eligibility
  '/portal/consumers/identities', // IDV detour submits (passport/licence/medicare/national-id, biometric applicants/checks, manual-IDV documents) — the shopper is blocked on these between IdentityIneligible and re-running startCheckout
  '/portal/consumers/apple-pay-sessions', // Apple Pay merchant validation — gates Apple Pay confirmation
  '/portal/consumers/apple-pay-token', // Apple Pay MPAN capture — gates Apple Pay confirmation
  '/portal/consumers/threeDS', // 3DS initiate/finalise — sits inside payment confirmation
  '/portal/consumers/registrations', // net-new signup: create/verify/confirm all block first payment
  '/portal/consumers/emails/lookup', // identity lookup — gates login-vs-signup routing (LoginIdentity, CreateAccount, initialise orchestrator)
  '/portal/consumers/shipping-addresses', // express-mode list/save/update (incl. /{token}/cash variant); saving blocks navigation to summary
  '/portal/force-password-reset', // forced reset send-email — blocks login until completed (note: not under /consumers)
  '/portal/consumers/efa-sms', // extended-fraud-alert code send — blocks login
  '/portal/consumers/efa-verify-code', // extended-fraud-alert code verify — blocks login
]

/** Logout is fire-and-forget and never blocks checkout progress */
const JOURNEY_IRRELEVANT_PATHS = ['/portal/consumers/auth/logout']

/** Exact match or sub-path — `/portal/consumers/account` must not match a future `/portal/consumers/accounts-unrelated` */
const matchesPath = (url: string, path: string): boolean => url === path || url.startsWith(`${path}/`)

export const isJourneyRelevantRequest = (url: string | undefined): boolean =>
  url !== undefined && JOURNEY_RELEVANT_PATHS.some((path) => matchesPath(url, path)) && !JOURNEY_IRRELEVANT_PATHS.some((path) => matchesPath(url, path))

/**
 * The canonical status mapping from the UJ instrumentation guide, with one deviation:
 * no response at all (offline, DNS failure, timeout) is device connectivity — expected
 * at scale, so friction rather than frustration — and 429 is friction because every
 * known portal 429 is an expected protective condition, not a fault: OTP resend limits
 * (the thunk rewrites the bare Dropwizard body to TooManyOTPRequests only after this
 * interceptor has run), payment-options hammer protection, and Kasada bot blocks. All
 * arrive WITHOUT a portal errorCode envelope, so the structured-body exemption in
 * reportJourneyNetworkError can't catch them; the errorCode-level flows own the
 * recovery UX, and Kasada blocks are independently monitored via
 * TrackingEvent.KASADA_BLOCK, so friction keeps rate limits visible on dashboards
 * without dipping the SLI.
 *
 * Kasada 429s stay friction even though they are cheap to single out
 * (handleKasadaBlockResponseError already detects them before this interceptor runs):
 * a Kasada block on a critical thunk already dips the SLI — the rejection carries no
 * errorCode, so handleCheckoutRejected falls back to TerminalErrorCode.Default, which
 * the terminal anchor maps to FAILED. The remaining cohort — blocks on retryable paths
 * inside journeys that later abandon — skews headless-browser bots (a journey requires
 * a rendered app, LD bootstrap, and a mounted IndexEntry), and frustration is sticky
 * for the journey lifetime, so tagging it would turn every mitigated attack wave into
 * a BAD spike on non-customer traffic. A mass false-positive event still surfaces via
 * the KASADA_BLOCK monitors and the <5% UNKNOWN ramp gate.
 */
export const classifyNetworkError = (status: number | undefined): NetworkSignal => {
  if (status === undefined || status === 401 || status === 408 || status === 429) {
    return { signalType: 'friction', signal: NETWORK_SIGNALS.CLIENT }
  }
  if (status >= 400 && status < 500) {
    return { signalType: 'frustration', signal: NETWORK_SIGNALS.CLIENT }
  }
  if (status >= 500 && status < 600) {
    return { signalType: 'frustration', signal: NETWORK_SIGNALS.SERVER }
  }
  return { signalType: 'frustration', signal: NETWORK_SIGNALS.UNKNOWN }
}

type NetworkSignalHandler = (signal: NetworkSignal) => void

// Module-level handler slot bridging the (non-React) axios interceptor into the React
// journey context. Registered by useJourneyNetworkSignals inside the provider tree;
// signals emitted with no handler mounted are dropped, which is correct — no provider
// means no journey to attach them to.
let networkSignalHandler: NetworkSignalHandler | undefined

export const setNetworkSignalHandler = (handler: NetworkSignalHandler | undefined): void => {
  networkSignalHandler = handler
}

/**
 * Entry point for the portal axios response-error interceptor. The interceptor runs on
 * every axios-retry attempt, so only the first attempt (retryCount 0) is classified —
 * the same dedup the AXIOS_NETWORK_ERROR log uses. A request that later succeeds on
 * retry keeps its signal: the customer still experienced the failure. The 401 →
 * persistent-login resume → replay path re-enters the interceptor as a fresh logical
 * request (retryCount 0), so a failing replay tags a second signal — accepted: the
 * session hiccup and the replay failure are distinct customer-visible events, and
 * friction never affects BAD classification. Must never throw — instrumentation must
 * not alter request error flow.
 */
export const reportJourneyNetworkError = (error: unknown): void => {
  if (!(error instanceof Error) || !isAxiosError(error)) {
    return
  }
  // A cancelled request is a deliberate abort (navigation, unmount), not a network failure
  if (error.code === 'ERR_CANCELED') {
    return
  }
  const retryCount = error.config?.['axios-retry']?.retryCount ?? 0
  if (retryCount > 0 || !isJourneyRelevantRequest(error.config?.url)) {
    return
  }
  // A 4xx carrying a structured errorCode body is a business response (decline, limit,
  // validation), not a transport failure — the errorCode-level mapping
  // (terminalErrorMapping, recoverable flows) owns its friction/frustration
  // classification. Tagging it here as frustration would make expected declines dip the
  // SLI. A 5xx is a server fault regardless of body shape, so it always classifies.
  // 401/408 stay classified even inside a portal envelope: 'unauthorized' has no
  // errorCode-level mapping (the interceptor short-circuits to persistent-login resume
  // or session-expiry/login, never /oh-no), so skipping would silently drop the
  // session-hiccup signal — and both map to friction, never frustration, so no SLI risk.
  const status = error.response?.status
  if (status !== undefined && status !== 401 && status !== 408 && status < 500 && isPortalApiError(error.response?.data)) {
    return
  }
  try {
    networkSignalHandler?.(classifyNetworkError(status))
  } catch {
    // A faulty handler must not break the interceptor's error flow
  }
}

/**
 * Bridge hook — mounted from CheckoutJourneyAnchors. Attaches classified network signals
 * to the checkout journey; the reducer no-ops when no journey is in flight (flag off,
 * pre-start, post-completion), so no gating is needed here.
 */
export const useJourneyNetworkSignals = (): void => {
  const { addFriction, addFrustration } = useUserJourneyContext()

  useEffect(() => {
    setNetworkSignalHandler(({ signalType, signal }) => {
      if (signalType === 'friction') {
        addFriction(USER_JOURNEY_NAMES.CHECKOUT, signal)
      } else {
        addFrustration(USER_JOURNEY_NAMES.CHECKOUT, signal)
      }
    })
    return () => {
      setNetworkSignalHandler(undefined)
    }
  }, [addFriction, addFrustration])
}
